import React, { PropsWithChildren, useCallback } from 'react'
import DatePicker from 'react-datepicker'
import { useParams, NavLink } from 'react-router-dom'
import { Form, Formik } from 'formik'
import {
  faChevronDown,
  faSearch,
  faDownload,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'

import Button from 'components/Button'
import Dropdown, { OptionType } from 'components/Dropdown'

import { isVisibleUI } from 'setups/controller'
import { TRANSACTION_TYPES } from 'constants/filters'
import { formatDateToShort } from 'services/helpers'
import { DropDownItem } from 'types/components'

import useTransactionsFilter from './useTransactionsFilter'
import useTransactionsMerchantLookup from './useTransactionsMerchantLookup'

type DatePickerProps = React.InputHTMLAttributes<HTMLInputElement> & {
  value?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

function Transactions(): React.ReactElement {
  const { traderId } = useParams()
  const { merchantLookupDropdownOptions } = useTransactionsMerchantLookup({
    memberTraderId: String(traderId),
  })

  const {
    handleTransactionFilter,
    handleTransactionFilterClear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    setSelectedMerchant,
    initialValues,
    isFilteredTransactions,
    transactionsLoading,
    transactionTypeId,
    merchantId,
  } = useTransactionsFilter({ memberTraderId: String(traderId) })

  const downloadSearchParams = new URLSearchParams()
  if (startDate && endDate) {
    downloadSearchParams.set('startDate', formatDateToShort(startDate))
    downloadSearchParams.set('endDate', formatDateToShort(endDate))
  }
  if (transactionTypeId)
    downloadSearchParams.set('transactionTypeId', String(transactionTypeId))
  if (merchantId) downloadSearchParams.set('merchantId', String(merchantId))

  const onChangeDatepicker = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
  }

  const formatDate = (): string => {
    if (startDate && endDate) {
      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
    }
    if (startDate) {
      return `${startDate.toLocaleDateString()}`
    }
    return ''
  }

  const DatePickerInput = ({ onClick }: PropsWithChildren<DatePickerProps>) => (
    <button
      className="p-2 pr-10 text-left border border-gray-400"
      style={{ minWidth: '120px' }}
      onClick={onClick}
      type="button"
    >
      {formatDate()}
      <Icon
        icon={faChevronDown}
        className="absolute top-0 right-0 h-full ml-4 mr-2"
      />
    </button>
  )

  const merchantLoadOptions = useCallback(
    async (inputValue: string): Promise<DropDownItem[]> =>
      merchantLookupDropdownOptions(inputValue),
    [merchantLookupDropdownOptions]
  )

  return (
    <Formik initialValues={initialValues} onSubmit={handleTransactionFilter}>
      {({ values, setFieldValue }) => (
        <Form>
          <div className="flex flex-wrap items-end mt-4 mb-5 gap-2">
            <Dropdown
              title="Merchants"
              value={values.merchant}
              loadOptions={merchantLoadOptions}
              onChange={(option: OptionType) => {
                if (option) {
                  setSelectedMerchant(option)
                  setFieldValue('merchant', option.value)
                } else {
                  setSelectedMerchant(null)
                  setFieldValue('merchant', '')
                }
              }}
              className="w-80"
              borderColor="#C4C4C4"
              containerClassName="focus:border-primary"
              placeholder="Search by merchant name..."
              isClearable
              isSearchable
              async
            />

            <div className="flex flex-col gap-2">
              <p>
                <span className="tracking-wider font-light">Date Range</span>
              </p>
              <DatePicker
                title="Date Picker"
                name="dateRange"
                onChange={onChangeDatepicker}
                startDate={startDate}
                endDate={endDate}
                className="p-2 border border-gray-400"
                customInput={<DatePickerInput />}
                selectsRange
                isClearable={false}
                showYearDropdown
                scrollableYearDropdown
              />
            </div>

            <Dropdown
              title="Transaction Type"
              value={values.transactionType}
              options={TRANSACTION_TYPES}
              onSelect={(option: DropDownItem) =>
                setFieldValue('transactionType', option)
              }
              className="w-40"
              borderColor="#C4C4C4"
            />

            <Button
              type="submit"
              icon={faSearch}
              className="ml-1 rounded-lg"
              iconRight={false}
              disabled={transactionsLoading}
            >
              <span className="tracking-wider font-bold">Filter</span>
            </Button>

            {isVisibleUI.member.transaction.download_transaction && (
              <Button
                type="button"
                icon={faDownload}
                className="ml-1 rounded-lg"
                iconRight={false}
              >
                <NavLink
                  to={`/reports/${traderId}/member-transactions-reports?${downloadSearchParams}`}
                >
                  <span className="tracking-wider font-bold">Download</span>
                </NavLink>
              </Button>
            )}

            {isFilteredTransactions && (
              <Button
                type="reset"
                className="ml-2 rounded-lg"
                iconRight={false}
                onClick={handleTransactionFilterClear}
                disabled={transactionsLoading}
              >
                <span className="tracking-wider font-bold">Clear</span>
              </Button>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default Transactions
